import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { getAuthHeaders } from "./auth";
import { useAppGlass } from "./glass";

function openUrl(url) {
  if (url) window.location.assign(url);
}

export default function BillingPanel() {
  const g = useAppGlass();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get("/billing/status", { headers: getAuthHeaders() });
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.message || "Could not load billing status.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("checkout") !== "success") return;
    setShowCheckoutSuccess(true);
    load();
    sp.delete("checkout");
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
  }, [location.search, load, navigate, location.pathname]);

  const postAction = async (path, body, busyKey) => {
    setBusy(busyKey || path);
    setError("");
    try {
      const { data } = await axios.post(path, body || {}, { headers: getAuthHeaders() });
      if (data.url) openUrl(data.url);
      else await load();
    } catch (e) {
      setError(e.response?.data?.message || "Request failed.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sub = status?.subscription;
  const conn = status?.connect;
  const stripeOk = status?.stripeConfigured;

  return (
    <Stack spacing={3}>
      {showCheckoutSuccess ? (
        <Alert severity="success" onClose={() => setShowCheckoutSuccess(false)}>
          Subscription checkout completed. Your plan should update in a few seconds once Stripe confirms the webhook.
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {!stripeOk ? (
        <Alert severity="warning">
          Stripe billing is not configured on this server yet. Set <strong>STRIPE_SECRET_KEY</strong> and price IDs in the API environment, then restart the server. Webhooks require{" "}
          <strong>STRIPE_WEBHOOK_SECRET</strong>.
        </Alert>
      ) : null}

      <Card elevation={0} sx={{ ...g.panel, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Your Clibbins subscription
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            You are billed by Clibbins for using the operator dashboard, quote tools, and embed. Choose a plan and pay securely with Stripe (card). A trial period applies when configured on the server.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Status:</strong> {sub?.status && sub.status !== "none" ? sub.status : "No active subscription"}
            </Typography>
            {sub?.planKey ? (
              <Typography variant="body2">
                <strong>Plan:</strong> {sub.planKey}
              </Typography>
            ) : null}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button variant="contained" disabled={!stripeOk || busy} onClick={() => postAction("/billing/checkout", { planKey: "solo" }, "checkout")}>
              {busy === "checkout" ? <CircularProgress size={22} color="inherit" /> : "Subscribe — Solo"}
            </Button>
            <Button variant="contained" color="secondary" disabled={!stripeOk || busy} onClick={() => postAction("/billing/checkout", { planKey: "growing" }, "checkout")}>
              {busy === "checkout" ? <CircularProgress size={22} color="inherit" /> : "Subscribe — Growing"}
            </Button>
            <Button variant="outlined" disabled={!stripeOk || busy || !sub?.customerId} onClick={() => postAction("/billing/portal", {}, "portal")}>
              {busy === "portal" ? <CircularProgress size={22} /> : "Manage subscription & invoices"}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Enterprise or custom crews: contact us from the marketing site or your success channel — checkout is not used for custom deals.
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ ...g.panel, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Bill your customers with Stripe
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Connect a <strong>Stripe Express</strong> account so Clibbins can route customer payments to you (deposits, job invoices, and future quote checkout). Clibbins does not take a platform fee unless you configure one later — you keep control of payouts in the Stripe Dashboard.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Connect:</strong> {conn?.connected ? "Linked" : "Not linked"}
            </Typography>
            {conn?.connected ? (
              <>
                <Typography variant="body2">
                  <strong>Charges:</strong> {conn.chargesEnabled ? "Enabled" : "Pending setup"}
                </Typography>
                <Typography variant="body2">
                  <strong>Payouts:</strong> {conn.payoutsEnabled ? "Enabled" : "Pending"}
                </Typography>
              </>
            ) : null}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button variant="contained" disabled={!stripeOk || busy} onClick={() => postAction("/billing/connect/onboard", {}, "connect-onboard")}>
              {busy === "connect-onboard" ? <CircularProgress size={22} color="inherit" /> : conn?.connected ? "Update Stripe connection" : "Connect Stripe"}
            </Button>
            <Button variant="outlined" disabled={!stripeOk || busy || !conn?.connected} onClick={() => postAction("/billing/connect/login", {}, "connect-login")}>
              {busy === "connect-login" ? <CircularProgress size={22} /> : "Open Stripe Dashboard"}
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Job-to-payment flows (charging a customer from a quote or job record) can call Stripe using this connected account. That wiring is incremental — this screen completes account onboarding first.
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}
